/**
 * Subscription & add-ons — the two writes support runs most often, on the user
 * already loaded. No email field: the account in context is the target.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Minus, Plus } from "lucide-react";
import {
  ADDON_GROUPS,
  ASSIGN_REASONS,
  PRODUCT_PLANS,
  type UserAccount,
} from "../../../../../shared/index";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Card, ConfirmDialog } from "./parts";
import { describeError, formatDate } from "./format";

const OTHER = "other";

const BASIC_PLAN = 1;
const DURATION_UNITS = ["day", "month", "year"] as const;
type DurationUnit = (typeof DURATION_UNITS)[number];

/** `count` units from today, as the `YYYY-MM-DD` the upstream API expects. */
function expiryFromDuration(count: number, unit: DurationUnit): string {
  const d = new Date();
  if (unit === "day") d.setDate(d.getDate() + count);
  if (unit === "month") d.setMonth(d.getMonth() + count);
  if (unit === "year") d.setFullYear(d.getFullYear() + count);
  return d.toISOString().slice(0, 10);
}

const selectClass =
  "h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

/** Plan/add-on changes are audited upstream, so a reason is always required. */
function ReasonPicker({
  reason,
  freeReason,
  onReason,
  onFreeReason,
  error,
}: {
  reason: string;
  freeReason: string;
  onReason: (v: string) => void;
  onFreeReason: (v: string) => void;
  error?: string;
}) {
  return (
    <div className="space-y-2">
      <Label>Reason</Label>
      <select
        className={cn(
          selectClass,
          error && "border-destructive focus-visible:ring-destructive"
        )}
        value={reason}
        onChange={(e) => onReason(e.target.value)}
      >
        <option value="">Select a reason…</option>
        {ASSIGN_REASONS.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
        <option value={OTHER}>Other (free text)</option>
      </select>
      {reason === OTHER && (
        <Input
          value={freeReason}
          onChange={(e) => onFreeReason(e.target.value)}
          placeholder="Describe the reason"
          className={cn(error && "border-destructive focus-visible:ring-destructive")}
        />
      )}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}

function resolveReason(reason: string, freeReason: string): string {
  return reason === OTHER ? freeReason.trim() : reason;
}

export function SubscriptionTab({ account }: { account: UserAccount }) {
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["site-user", account.email] });

  // ── Assign plan ───────────────────────────────────────────────
  const [plan, setPlan] = useState<number>(PRODUCT_PLANS[0].value);
  const [durationCount, setDurationCount] = useState("1");
  const [durationUnit, setDurationUnit] = useState<DurationUnit>("year");
  const [planReason, setPlanReason] = useState("");
  const [planFreeReason, setPlanFreeReason] = useState("");
  const [confirmPlan, setConfirmPlan] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isBasic = plan === BASIC_PLAN;
  const count = Number(durationCount);
  const countValid =
    durationCount.trim() === "" ||
    (Number.isInteger(count) && count >= 1 && count <= 99);
  // Empty duration means one year, same as Create user.
  const expiry = expiryFromDuration(
    durationCount.trim() === "" || !countValid ? 1 : count,
    durationCount.trim() === "" ? "year" : durationUnit
  );

  const assign = useMutation({
    mutationFn: () =>
      api.siteUsers.assignSubscription({
        email: account.email,
        productPlan: plan,
        expiryDate: expiry,
        reason: resolveReason(planReason, planFreeReason),
      }),
    onSuccess: (r) => {
      toast.success(r.message);
      setConfirmPlan(false);
      refresh();
    },
    onError: (e) => toast.error(describeError(e)),
  });

  // ── Add-ons ───────────────────────────────────────────────────
  const [addOn, setAddOn] = useState<number>(ADDON_GROUPS[0].value);
  const [addOnReason, setAddOnReason] = useState("");
  const [addOnFreeReason, setAddOnFreeReason] = useState("");

  const addOnMutation = useMutation({
    mutationFn: (mode: "add" | "remove") => {
      const body = {
        email: account.email,
        addOnGroup: addOn,
        reason: resolveReason(addOnReason, addOnFreeReason),
      };
      return mode === "add"
        ? api.siteUsers.addAddOn(body)
        : api.siteUsers.removeAddOn(body);
    },
    onSuccess: (r) => {
      toast.success(r.message);
      refresh();
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const planErrors = {
    reason: resolveReason(planReason, planFreeReason)
      ? undefined
      : "Pick a reason, or describe one",
    duration: countValid ? undefined : "A whole number between 1 and 99",
  };
  const planError = (f: keyof typeof planErrors) =>
    submitted ? planErrors[f] : undefined;
  const errorClass = (f: keyof typeof planErrors) =>
    planError(f) && "border-destructive focus-visible:ring-destructive";
  const addOnReasonOk = resolveReason(addOnReason, addOnFreeReason).length > 0;
  const planLabel =
    PRODUCT_PLANS.find((p) => p.value === plan)?.label ?? String(plan);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card
        title="Assign subscription"
        description={`Current plan: ${account.productPlanName ?? "none"}, expires ${formatDate(account.expiration)}`}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Plan</Label>
            <select
              className={selectClass}
              value={plan}
              onChange={(e) => setPlan(Number(e.target.value))}
            >
              {PRODUCT_PLANS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          {!isBasic && (
            <div className="space-y-2">
              <Label htmlFor="durationCount">Subscription length</Label>
              <div className="flex gap-2">
                <Input
                  id="durationCount"
                  type="number"
                  min={1}
                  max={99}
                  value={durationCount}
                  onChange={(e) => setDurationCount(e.target.value)}
                  className={cn("w-20", errorClass("duration"))}
                />
                <select
                  className={selectClass}
                  value={durationUnit}
                  onChange={(e) => setDurationUnit(e.target.value as DurationUnit)}
                  aria-label="Duration unit"
                >
                  {DURATION_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                      {Number(durationCount) === 1 ? "" : "s"}
                    </option>
                  ))}
                </select>
              </div>
              {planError("duration") ? (
                <p className="text-xs font-medium text-destructive">
                  {planError("duration")}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {durationCount.trim() === ""
                    ? `Leave empty for 1 year — expires ${expiry}`
                    : `Expires ${expiry}`}
                </p>
              )}
            </div>
          )}
          <ReasonPicker
            reason={planReason}
            freeReason={planFreeReason}
            onReason={setPlanReason}
            onFreeReason={setPlanFreeReason}
            error={planError("reason")}
          />
          <Button
            className="w-full"
            disabled={assign.isPending}
            onClick={() => {
              setSubmitted(true);
              if (Object.values(planErrors).every((e) => !e)) setConfirmPlan(true);
              else toast.error("Some fields still need attention");
            }}
          >
            {assign.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Assign {planLabel}
          </Button>
        </div>
      </Card>

      <Card
        title="Add-ons"
        description={
          account.addOns.length
            ? `Active: ${account.addOns.map((a) => a.name).join(", ")}`
            : "No add-ons on this account"
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Add-on group</Label>
            <select
              className={selectClass}
              value={addOn}
              onChange={(e) => setAddOn(Number(e.target.value))}
            >
              {ADDON_GROUPS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
          <ReasonPicker
            reason={addOnReason}
            freeReason={addOnFreeReason}
            onReason={setAddOnReason}
            onFreeReason={setAddOnFreeReason}
          />
          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={!addOnReasonOk || addOnMutation.isPending}
              onClick={() => addOnMutation.mutate("add")}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              disabled={!addOnReasonOk || addOnMutation.isPending}
              onClick={() => addOnMutation.mutate("remove")}
            >
              <Minus className="mr-2 h-4 w-4" />
              Remove
            </Button>
          </div>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmPlan}
        onOpenChange={setConfirmPlan}
        title="Assign subscription"
        description={
          <>
            <p>
              <span className="font-medium">{account.email}</span> will be moved
              to <span className="font-medium">{planLabel}</span> until{" "}
              <span className="font-medium">{expiry || "—"}</span>.
            </p>
            <p className="text-muted-foreground">
              This changes the live account immediately.
            </p>
          </>
        }
        confirmLabel="Assign"
        pending={assign.isPending}
        onConfirm={() => assign.mutate()}
      />
    </div>
  );
}
